import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { formatCurrency, shortMonth } from '../mocks/wipAiFixture'

const Section = styled.section`
  margin-top: 28px;
`

const Card = styled.div`
  background: ${p => p.theme.colors.semantic.surface};
  border: 1px solid ${p => p.theme.colors.semantic.border};
  border-radius: 12px;
  box-shadow: ${p => p.theme.colors.semantic.shadowSm};
  padding: 0;
  overflow: hidden;
`

const TblHead = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid ${p => p.theme.colors.semantic.border};
  gap: 12px;
`

const HeadT = styled.span`
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`

const HeadSub = styled.span`
  font-size: 12px;
  color: ${p => p.theme.colors.semantic.textMuted};
  margin-left: auto;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th,
  td {
    padding: 10px 18px;
    text-align: left;
    border-bottom: 1px solid ${p => p.theme.colors.semantic.border};
  }
  th {
    font-family: ${p => p.theme.fontFamily.sans.join(', ')};
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${p => p.theme.colors.semantic.textMuted};
    background: ${p => p.theme.colors.semantic.surfaceRaised};
  }
  td.num,
  th.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
    font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  }
  tr:last-child td {
    border-bottom: none;
  }
  tr:hover td {
    background: ${p => p.theme.colors.semantic.surfaceRaised};
  }
  tr.total td {
    font-weight: 700;
    background: ${p => p.theme.colors.semantic.surfaceRaised};
  }
  tr.total td:first-child {
    font-family: ${p => p.theme.fontFamily.sans.join(', ')};
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
`

const Cur = styled.span`
  margin-left: 6px;
  padding: 1px 6px;
  background: ${p => p.theme.colors.semantic.successTint};
  color: ${p => p.theme.colors.semantic.success};
  font-size: 9px;
  border-radius: 3px;
`

const WipAiFinancialSummaryTable = ({ monthlyReports, currentMonth, iso }) => {
  const idx = monthlyReports.findIndex(r => r.month === currentMonth)
  const months = monthlyReports.slice(idx, idx + 3)

  const rows = [
    {
      key: 'staffFees',
      label: 'Staff Fees',
      pick: r => r.submittedHours.staffRate,
      variance: false,
      muted: false,
      bold: false,
    },
    {
      key: 'staffExp',
      label: 'Staff Expenses',
      pick: r => r.submittedExpenses.staffExpenses,
      variance: false,
      muted: false,
      bold: false,
    },
    {
      key: 'total',
      label: 'Total',
      pick: r => r.submittedHours.staffRate + r.submittedExpenses.staffExpenses,
      variance: false,
      muted: false,
      bold: true,
    },
    {
      key: 'budget',
      label: 'Budget',
      pick: r => r.budget,
      variance: false,
      muted: true,
      bold: false,
    },
    {
      key: 'variance',
      label: 'Variance',
      pick: r => r.submittedHours.staffRate + r.submittedExpenses.staffExpenses - r.budget,
      variance: true,
      muted: false,
      bold: false,
    },
  ]

  return (
    <Section id="financials">
      <Card>
        <TblHead>
          <HeadT>Financial Summary</HeadT>
          <HeadSub>Current month + next 2 · {iso}</HeadSub>
        </TblHead>
        <Table className="ks">
          <thead>
            <tr>
              <th />
              {months.map(m => (
                <th key={m.month} className="num">
                  {shortMonth(m.month)}
                  {m.month === currentMonth && <Cur>CURRENT</Cur>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.key} className={row.bold ? 'total' : ''}>
                <td style={row.muted ? { color: '#676767' } : {}}>{row.label}</td>
                {months.map(m => {
                  const v = row.pick(m)
                  if (row.variance) {
                    return (
                      <td
                        key={m.month}
                        className={`num ${v > 0 ? 'var-neg' : 'var-pos'}`}
                        style={{ color: v > 0 ? '#D0342C' : '#00A162' }}
                      >
                        {v === 0 ? '—' : `${v > 0 ? '+' : ''}${formatCurrency(v, iso)}`}
                      </td>
                    )
                  }
                  return (
                    <td key={m.month} className="num" style={row.muted ? { color: '#676767' } : {}}>
                      {v === 0 ? '—' : formatCurrency(v, iso)}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
    </Section>
  )
}

WipAiFinancialSummaryTable.propTypes = {
  monthlyReports: PropTypes.arrayOf(PropTypes.object).isRequired,
  currentMonth: PropTypes.string.isRequired,
  iso: PropTypes.string.isRequired,
}

export default WipAiFinancialSummaryTable
